import styled from "@emotion/styled";
import Linkify from "linkify-react";
import React from "react";
import { match } from "ts-pattern";
import {
  CommCenterMessageActionId,
  CommCenterMessagePayload,
  CommCenterTicketId,
} from "../../../messages";
import { assertNotNull } from "../../../utils/assertUndefinedable";
import { CommCenterListActionItem } from "../utils/chatUtils";
import ChatMessageDocument from "./ChatMessageDocument";
import ChatMessageImage from "./ChatMessageImage";
import ChatMessageList from "./ChatMessageList";
import ChatMessageVideo from "./ChatMessageVideo";

interface Props {
  ticketId: CommCenterTicketId;
  payload: CommCenterMessagePayload;
  showActionsList: boolean;
  isLatestMessage: boolean;
  imagePreview: string | null;
  onClickActionItem: (item: CommCenterListActionItem, actionId: CommCenterMessageActionId) => void;
  onRequestImagePreview: (image: string | null) => void;
}

function ChatMessageContent(props: Props) {
  return (
    <ChatMessageContent.Root>
      {props.payload.map((part, i) => (
        <ChatMessageContent.Item key={i}>
          {match(part)
            .with({ type: "TEXT" }, (data) => (
              <Linkify options={{ target: "_system" }}>{data.message}</Linkify>
            ))
            .with({ type: "IMAGE" }, (data) => (
              <ChatMessageImage
                src={data.url}
                urlWithoutQuery={data.url.split("?")[0]}
                imagePreview={props.imagePreview}
                onRequestImagePreview={props.onRequestImagePreview}
              />
            ))
            .with({ type: "DOCUMENT" }, (data) => <ChatMessageDocument src={data.url} />)
            .with({ type: "VIDEO" }, (data) => <ChatMessageVideo src={data.url} />)
            .with({ type: "ACTION", payload: { payloadType: "TEXT" } }, (data) => (
              <>{data.payload.text}</>
            ))
            .with({ type: "ACTION", payload: { payloadType: "IMAGE" } }, (data) => (
              <img src={data.payload.url} />
            ))
            .with({ type: "ACTION", payload: { payloadType: "DOCUMENT" } }, (data) => (
              <ChatMessageDocument src={data.payload.url} />
            ))
            .with({ type: "ACTION", payload: { payloadType: "VIDEO" } }, (data) => (
              <ChatMessageVideo src={data.payload.url} />
            ))
            .with({ type: "ACTION", payload: { payloadType: "LIST" } }, (data) =>
              props.showActionsList ? (
                <ChatMessageList
                  list={data.payload}
                  ticketId={props.ticketId}
                  actionId={assertNotNull(data.id)}
                  isLatestMessage={props.isLatestMessage}
                  onClickActionItem={props.onClickActionItem}
                />
              ) : null
            )
            .otherwise(() => null)}
        </ChatMessageContent.Item>
      ))}
    </ChatMessageContent.Root>
  );
}

ChatMessageContent.Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

ChatMessageContent.Item = styled.div`
  font-size: 16px;
`;

export default ChatMessageContent;
